import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';

import { StagingRowTable } from './components/StagingRowTable';
import { StagingRowDrawer } from './components/StagingRowDrawer';
import { ImportSheetsSummary } from './components/ImportSheetsSummary';
import { PromoteAction } from './components/PromoteAction';
import { useImportJob } from './useImportJob';
import { useStagingRows } from './useStagingRows';
import {
  stagingRowStatusValues,
  stagingRowTypes,
  type StagingRowDto,
  type StagingRowStatus,
  type StagingRowType,
} from './types';

const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];
const ALL = 'all' as const;
type AllOr<T extends string> = typeof ALL | T;

export const ImportReviewPage = () => {
  const { t } = useTranslation();
  const { jobId } = useParams();

  const [rowType, setRowType] = useState<AllOr<StagingRowType>>(ALL);
  const [status, setStatus] = useState<AllOr<StagingRowStatus>>(ALL);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [selectedRow, setSelectedRow] = useState<StagingRowDto | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const job = useImportJob(jobId);
  const stagingRows = useStagingRows(jobId, {
    row_type: rowType === ALL ? undefined : rowType,
    status: status === ALL ? undefined : status,
    page,
    per_page: perPage,
  });

  const handleRowClick = (row: StagingRowDto) => {
    setSelectedRow(row);
    setDrawerOpen(true);
  };

  const total = stagingRows.data?.meta?.total ?? 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.imports.review.title', { ns: 'imports' })}
        subtitle={t('efs.imports.review.subtitle', { ns: 'imports' })}
        actions={<PromoteAction job={job.data} jobId={jobId} />}
      />

      {job.data ? (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">
            {t(`efs.imports.statuses.${job.data.status}`, {
              ns: 'imports',
              defaultValue: job.data.status,
            })}
          </Badge>
          <span className="font-mono text-xs text-efs-text-muted">{job.data.import_type}</span>
        </div>
      ) : null}

      <ImportSheetsSummary summary={job.data?.summary} />

      <TableToolbar
        count={t('efs.masters.toolbar.count', {
          ns: 'masters',
          count: total,
        })}
        filters={
          <>
            <label className="flex items-center gap-2 text-xs">
              <span>{t('efs.imports.review.filters.row_type', { ns: 'imports' })}</span>
              <Select
                value={rowType}
                onValueChange={(v) => {
                  setRowType(v as AllOr<StagingRowType>);
                  setPage(1);
                }}
              >
                <SelectTrigger
                  className="h-8 w-44"
                  aria-label={t('efs.imports.review.filters.row_type', { ns: 'imports' })}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>
                    {t('efs.imports.review.filters.all', { ns: 'imports' })}
                  </SelectItem>
                  {stagingRowTypes.map((rt) => (
                    <SelectItem key={rt} value={rt}>
                      {t(`efs.imports.row_types.${rt}`, {
                        ns: 'imports',
                        defaultValue: rt,
                      })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="flex items-center gap-2 text-xs">
              <span>{t('efs.imports.review.filters.status', { ns: 'imports' })}</span>
              <Select
                value={status}
                onValueChange={(v) => {
                  setStatus(v as AllOr<StagingRowStatus>);
                  setPage(1);
                }}
              >
                <SelectTrigger
                  className="h-8 w-40"
                  aria-label={t('efs.imports.review.filters.status', { ns: 'imports' })}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>
                    {t('efs.imports.review.filters.all', { ns: 'imports' })}
                  </SelectItem>
                  {stagingRowStatusValues.map((st) => (
                    <SelectItem key={st} value={st}>
                      {t(`efs.imports.row_statuses.${st}`, {
                        ns: 'imports',
                        defaultValue: st,
                      })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </>
        }
        actions={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setRowType(ALL);
              setStatus(ALL);
              setPage(1);
            }}
          >
            {t('efs.imports.review.filters.all', { ns: 'imports' })}
          </Button>
        }
      />

      <StagingRowTable
        rows={stagingRows.data?.rows ?? []}
        isLoading={stagingRows.isLoading}
        page={page}
        perPage={perPage}
        total={total}
        onPageChange={setPage}
        onPerPageChange={(nextPerPage) => {
          setPage(1);
          setPerPage(nextPerPage);
        }}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onRowClick={handleRowClick}
      />

      <StagingRowDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        row={selectedRow}
        jobId={jobId}
      />
    </div>
  );
};

export default ImportReviewPage;
